import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Who you are, and the one thing you do about it.
 *
 * The three items in this corner used to sit in a flat row at equal weight: a
 * bordered portal select, two lines of text, and a bordered Sign out button.
 * They are not equal — the portal switcher changes *what you are looking at*,
 * the name says *who you are*, and signing out is a rare, mildly destructive
 * action. Flattened together they read as one cluster of controls, and Sign out
 * competed with the page's own primary button for attention.
 *
 * So: identity collapses to an initials mark and a name, and the action moves
 * inside the menu it belongs to. The portal switcher stays outside, separated by
 * a rule, because it is a different question.
 */
export function ProfileMenu(
  { name, subtitle, email, signOutLabel = 'Sign out', onSignOut, children }:
  {
    name: string;
    /** Role, or the account they act for. One line, under the name. */
    subtitle: string;
    email?: string;
    /** Translated on the customer side; the console is English by decision. */
    signOutLabel?: string;
    onSignOut: () => void;
    /** Anything else the menu should carry, above Sign out. */
    children?: ReactNode;
  },
) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocument = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // Pointer down, not click: a menu that survives until mouseup flickers when
    // you press on the page behind it.
    document.addEventListener('mousedown', onDocument);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocument);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /** Initials from the name as given. Two at most, and never from an email. */
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div className="mk-profile" ref={wrap}>
      <button
        type="button"
        className="mk-profile-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="mk-avatar" aria-hidden>{initials}</span>
        <span className="mk-profile-id">
          <b>{name}</b>
          <span>{subtitle}</span>
        </span>
        <svg
          className={`mk-chev ${open ? 'on' : ''}`} width="10" height="10" viewBox="0 0 10 10"
          fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden
        >
          <path d="M1.5 3.5 5 7l3.5-3.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open ? (
        <div className="mk-menu" role="menu">
          <div className="mk-menu-head">
            <b>{name}</b>
            {email ? <span>{email}</span> : null}
            <span>{subtitle}</span>
          </div>
          {children ? <div className="mk-menu-body">{children}</div> : null}
          <button
            type="button" className="mk-menu-item" role="menuitem"
            onClick={() => { setOpen(false); onSignOut(); }}
          >
            {signOutLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
}
